<template>
    <EditorPreferencesTabs :features="features" tab-key="template-defaults">
        <form class="mb-[98px]" @submit.prevent="save">
            <section class="mt-5 grid-cols-layout border-b border-gray-300 pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3 class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800">
                        Layout and colors
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        What every new template starts with. Saved templates
                        keep their own settings. Leave a field empty to use the
                        editor default.
                    </p>
                </div>
                <div class="mx-auto w-full rounded-[6px] bg-white px-6 pb-6 pt-[23px] shadow-card lg:mx-0">
                    <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                        <TextInput
                            v-model="form.settings.emailWidth"
                            label="Email width (px)"
                            type="number"
                            placeholder="e.g. 600"
                            :disabled="!canUpdate"
                            :error="form.errors['settings.emailWidth']"
                        />
                        <TextInput
                            v-model="form.settings['line-height']"
                            label="Line height"
                            placeholder="e.g. 1.5"
                            :disabled="!canUpdate"
                            :error="form.errors['settings.line-height']"
                        />
                        <TextInput
                            v-model="form.settings['structure-margin']"
                            label="Space between rows"
                            placeholder="e.g. 15px"
                            :disabled="!canUpdate"
                            :error="form.errors['settings.structure-margin']"
                        />
                        <ColorField
                            v-model="form.settings.emailBackgroundColor"
                            label="Email background"
                            :disabled="!canUpdate"
                            :error="form.errors['settings.emailBackgroundColor']"
                        />
                        <ColorField
                            v-model="form.settings.blockBackgroundColor"
                            label="Content background"
                            :disabled="!canUpdate"
                            :error="form.errors['settings.blockBackgroundColor']"
                        />
                        <ColorField
                            v-model="form.settings.hyperlinkColor"
                            label="Link color"
                            :disabled="!canUpdate"
                            :error="form.errors['settings.hyperlinkColor']"
                        />
                        <SelectInput
                            class="sm:col-span-2"
                            label="Stack columns on mobile"
                            placeholder="Editor default"
                            :data="stackColumnsOptions"
                            :model-value="stackColumns"
                            :disabled="!canUpdate"
                            :error="form.errors['settings.stack-columns']"
                            unselectable
                            @update:modelValue="setStackColumns"
                        />
                    </div>
                </div>
            </section>

            <section
                v-for="section in textSections"
                :key="section.tag"
                class="mt-5 grid-cols-layout border-b border-gray-300 pb-5 lg:grid"
            >
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3 class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800">
                        {{ section.title }}
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ section.description }}
                    </p>
                </div>
                <div class="mx-auto w-full rounded-[6px] bg-white px-6 pb-6 pt-[23px] shadow-card lg:mx-0">
                    <TypographyFields
                        v-model="form.settings[section.tag]"
                        :font-options="fontOptions"
                        :error-prefix="`settings.${section.tag}`"
                        :errors="form.errors"
                        :disabled="!canUpdate"
                    />
                </div>
            </section>

            <section class="mt-5 grid-cols-layout border-b border-gray-300 pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3 class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800">
                        Buttons
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        How a newly added button looks.
                    </p>
                </div>
                <div class="mx-auto flex w-full flex-col gap-6 rounded-[6px] bg-white px-6 pb-6 pt-[23px] shadow-card lg:mx-0">
                    <ColorField
                        v-model="form.settings.buttonsBackgroundColor"
                        label="Button color"
                        :disabled="!canUpdate"
                        :error="form.errors['settings.buttonsBackgroundColor']"
                    />
                    <TypographyFields
                        v-model="form.settings.button"
                        :font-options="fontOptions"
                        error-prefix="settings.button"
                        :errors="form.errors"
                        :with-background="false"
                        with-radius
                        :disabled="!canUpdate"
                    />
                </div>
            </section>

            <div class="mt-4 flex flex-wrap justify-end gap-4 sm:flex-nowrap">
                <LockableButton
                    type="submit"
                    color="yellow"
                    size="md"
                    :disabled="!canUpdate || !form.isDirty || form.processing"
                    :locked="!hasAnyProSubscriptionOrTrial"
                    :lock-message="`You need a ${planName} subscription to update editor settings.`"
                >
                    Save changes
                </LockableButton>
            </div>
        </form>
    </EditorPreferencesTabs>
</template>

<script lang="ts" setup>
import { route } from 'ziggy-js';
import { PropType } from 'vue';
import { useForm } from '@inertiajs/vue3';
import ISelectable from '@/types/Atomic/ISelectable';
import IEditorSettings from '@/types/Settings/IEditorSettings';
import IDefaultTemplateSettings, {
    TextTag,
} from '@/types/Settings/IDefaultTemplateSettings';
import EditorPreferencesTabs from '../components/EditorPreferencesTabs.vue';
import useFonts from '../Fonts/composables/useFonts';
import { usableFontStyle } from './usableFontStyle';
import ColorField from './components/ColorField.vue';
import TypographyFields from './components/TypographyFields.vue';
import { create } from '@composables/useNotifications';
import { useSubscriptionLabel } from '@/composables/useSubscriptionLabel';

const props = defineProps({
    editorSettings: {
        type: Object as PropType<IEditorSettings>,
        required: true,
    },
    canUpdate: {
        type: Boolean,
        default: false,
    },
    features: {
        type: Object,
        default: null,
    },
});

const { hasAnyProSubscriptionOrTrial } = useSubscriptions();
const { planName } = useSubscriptionLabel();
const { defaultFonts } = useFonts();

const textSections: { tag: TextTag; title: string; description: string }[] = [
    { tag: 'h1', title: 'Heading 1', description: 'Main headings in text blocks.' },
    { tag: 'h2', title: 'Heading 2', description: 'Secondary headings.' },
    { tag: 'h3', title: 'Heading 3', description: 'Smaller headings.' },
    { tag: 'p', title: 'Paragraph', description: 'Body text in text blocks.' },
];

/**
 * The fonts the editor offers this team - its own list replaces the built-in
 * one. Values end up inside a style="" attribute, so they are re-quoted with
 * single quotes, and a font whose name the server would refuse is left out
 * rather than offered and then rejected on save.
 */
const fontOptions = computed<ISelectable[]>(() => {
    const base = props.editorSettings.default_fonts?.length
        ? props.editorSettings.default_fonts
        : defaultFonts;
    const custom = (props.editorSettings.custom_fonts ?? []).filter(
        (font) => font.enabled
    );

    const seen = new Set<string>();

    return [...base, ...custom]
        .map((font) => ({ ...font, style: usableFontStyle(font.style) }))
        .filter(
            (font): font is typeof font & { style: string } =>
                font.style !== null && !seen.has(font.style) && !!seen.add(font.style)
        )
        .map((font) => ({
            title: font.label || font.style.split(',')[0].replace(/'/g, ''),
            value: font.style,
        }));
});

const stackColumnsOptions: ISelectable[] = [
    { title: 'Stack columns', value: 'yes' },
    { title: 'Keep columns side by side', value: 'no' },
];

/** PHP serialises an empty set as a list, so treat that as nothing saved. */
const saved: IDefaultTemplateSettings = Array.isArray(
    props.editorSettings.default_template_settings
)
    ? {}
    : props.editorSettings.default_template_settings ?? {};

const form = useForm({
    settings: {
        emailWidth: saved.emailWidth ?? '',
        emailBackgroundColor: saved.emailBackgroundColor ?? '',
        blockBackgroundColor: saved.blockBackgroundColor ?? '',
        buttonsBackgroundColor: saved.buttonsBackgroundColor ?? '',
        hyperlinkColor: saved.hyperlinkColor ?? '',
        'line-height': saved['line-height'] ?? '',
        'structure-margin': saved['structure-margin'] ?? '',
        'stack-columns': saved['stack-columns'] ?? null,
        h1: { ...saved.h1 },
        h2: { ...saved.h2 },
        h3: { ...saved.h3 },
        p: { ...saved.p },
        button: { ...saved.button },
    },
});

const stackColumns = computed(() => {
    const value = form.settings['stack-columns'];

    return value === null ? '' : value ? 'yes' : 'no';
});

const setStackColumns = (value: string | null) => {
    form.settings['stack-columns'] = value === null ? null : value === 'yes';
};

const save = () => {
    form.put(route('editor-preferences.template-defaults.update'), {
        preserveScroll: true,
        onSuccess: () => {
            create({
                title: 'Template defaults updated',
                text: 'New templates will start with these settings.',
                type: 'success',
            });
        },
        onError: () => {
            create({
                title: 'Template defaults not updated',
                text: 'Please check the highlighted fields.',
                type: 'error',
            });
        },
    });
};
</script>
